import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PageHero, Reveal, Label, CTASection } from "../components/Shared";
import Ribbon from "../components/Ribbon";
import { IMAGES } from "../data/site";

const STATS = [
  { value: "510(k)", label: "FDA Clearance" },
  { value: "ISO 7", label: "Cleanroom Class" },
  { value: "ISO 8", label: "Ante Room Class" },
  { value: "cGMP", label: "Aligned Processes" },
];

const ParallaxStory = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-navy py-24 lg:py-32">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.whiteSuit}
          alt="MD Labs cleanroom operators"
          className="h-full w-full scale-110 object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-navy/70" />
      </motion.div>
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Label light>The Manufacturing Story</Label>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="font-heading text-3xl font-bold leading-[1.2] tracking-tight text-white sm:text-4xl">
                Handcrafted in a certified cleanroom — never mass-produced.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg leading-relaxed text-slate-300">
                Our suppositories are produced with deliberate care inside a
                controlled ISO 7 cleanroom environment. Air, particulates and
                process are all tightly controlled — the same rigor you'd expect
                from any regulated medical device operation.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-6 text-lg leading-relaxed text-slate-300">
                It's a different philosophy from high-volume commodity production:
                precision, traceability and documentation at every step.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function About() {
  return (
    <>
      <PageHero
        label="About MD Labs"
        titleLines={["A medical device", "company built on", "credibility."]}
        intro="MD Labs is an FDA-regulated medical device manufacturing facility in Westlake Village, California, specializing in vaginal suppositories produced in a certified ISO-classified cleanroom."
        image={IMAGES.gloves}
        imageAlt="Cleanroom operator in sterile gloves"
      />
      <Ribbon />

      <section className="bg-[#FAFAFA] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <Label>Mission</Label>
            </Reveal>
            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <p className="font-heading text-3xl font-bold leading-[1.25] tracking-tight text-navy sm:text-4xl">
                  To be the trusted, compliant manufacturing partner behind the
                  brands and providers we serve.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
                  We exist to give wellness brands, physicians and healthcare
                  companies a manufacturing foundation they can stand behind —
                  where FDA clearance, ISO certification and full documentation are
                  the baseline, not the exception.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <ParallaxStory />

      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal>
            <Label>Facility Overview</Label>
            <h2 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              An FDA 510(k) clearance is a statement of quality and safety.
            </h2>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              Holding a 510(k) clearance means our device has demonstrated
              substantial equivalence to a legally marketed device — and that it is
              manufactured under regulated, quality-controlled conditions. Our ISO 7
              cleanroom certification is a major credibility point, validating strict
              control of the manufacturing environment.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-2 border-t border-l border-slate-200 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal
                key={s.label}
                delay={i * 0.08}
                className="border-b border-r border-slate-200"
              >
                <div className="p-8 lg:p-10">
                  <div className="font-display text-4xl font-extrabold text-navy lg:text-5xl">
                    {s.value}
                  </div>
                  <div className="mt-3 text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
